import { capitalize } from "lodash";
import type {
  ApDocumentType,
  ApEmail,
  ApEmailResponse,
} from "@/accounts-payable/types";
import { dayjs } from "@/core/utils/date";
import { formatDollarsToCent } from "@/core/utils/money";

export const formatDollars = (n: number): string =>
  formatDollarsToCent(Number(n));

export const formatPercent = (value: number): string =>
  `${Math.round(value * 100)}%`;

export const HIGH_CONFIDENCE = 0.9;

export const LOW_CONFIDENCE = 0.7;

export type ApConfidenceTone = "low" | "normal" | "high";

export const confidenceTone = (value: number): ApConfidenceTone => {
  if (value < LOW_CONFIDENCE) return "low";
  return value >= HIGH_CONFIDENCE ? "high" : "normal";
};

export const confidenceLabel = (value: number): string =>
  `${formatPercent(value)} confidence`;

export const CONFIDENCE_TEXT_CLASS: Record<ApConfidenceTone, string> = {
  high: "text-success",
  normal: "text-warning",
  low: "text-danger",
};

export const formatApDate = (value?: string | null): string => {
  if (!value) return "–";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "–";
};

export const inZone = (value: dayjs.Dayjs, timeZone?: string) =>
  timeZone ? value.tz(timeZone) : value;

export const formatApZonedDate = (
  value: string | null | undefined,
  timeZone?: string,
): string => {
  if (!value) return "–";
  const parsed = dayjs(value);
  return parsed.isValid()
    ? inZone(parsed, timeZone).format("MMM D, YYYY")
    : "–";
};

export const formatApZonedDateTime = (
  value: string | null | undefined,
  timeZone?: string,
): string | undefined => {
  if (!value) return undefined;
  const parsed = dayjs(value);
  return parsed.isValid()
    ? inZone(parsed, timeZone).format("MMM D, YYYY h:mm A z")
    : undefined;
};

export const apToday = (timeZone?: string): string =>
  inZone(dayjs(), timeZone).format("YYYY-MM-DD");

export const documentListEmptyMessage = (
  kind: ApDocumentType,
  {
    range,
    field = "added",
    filtered,
    today,
  }: {
    range: { from: string; to: string } | null;
    field?: "added" | "reviewed";
    filtered: boolean;
    today: string;
  },
): { title: string; body: string } => {
  const plural = `${kind}s`;
  if (range) {
    const day = (value: string) => dayjs(value, "YYYY-MM-DD").format("MMM D");
    const when =
      range.from === range.to
        ? range.from === today
          ? "today"
          : `on ${day(range.from)}`
        : `between ${day(range.from)} and ${day(range.to)}`;
    const verb = field === "reviewed" ? "reviewed" : "added";
    const filterName = field === "reviewed" ? "Reviewed" : "Added";
    return {
      title: `No ${filtered ? "matching " : ""}${plural} ${verb} ${when}`,
      body: `Pick another day, or clear the ${filterName} filter to see them all.`,
    };
  }
  if (filtered) {
    return {
      title: `No ${plural} match these filters`,
      body: "Try other filters or another search.",
    };
  }
  return {
    title: `No ${plural} yet`,
    body: `${capitalize(plural)} emailed to your AP address or uploaded here will show up in this list.`,
  };
};

export const formatApDateTime = (
  value: string | null | undefined,
  timeZone: string | undefined,
): string => {
  if (!value) return "–";
  const parsed = dayjs(value);
  return parsed.isValid()
    ? inZone(parsed, timeZone).format("MMM D, h:mm A")
    : "–";
};

export const formatFileSize = (bytes: number): string => {
  if (!bytes) return "–";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

export const enrichApEmail = (email: ApEmailResponse): ApEmail => ({
  ...email,
  from: email.from ?? email.from_address ?? "",
  message:
    email.plain_body?.trim() ||
    email.html_body?.replace(/<[^>]+>/g, " ").trim(),
  invoiceCount: email.attachments.reduce(
    (count, attachment) => count + (attachment.invoice_ids?.length ?? 0),
    0,
  ),
  receiptCount: email.attachments.reduce(
    (count, attachment) => count + (attachment.receipt_ids?.length ?? 0),
    0,
  ),
});

export const fileKind = (contentType: string): string => {
  if (contentType === "application/pdf") return "PDF";
  if (contentType.startsWith("image/"))
    return contentType.split("/")[1].toUpperCase();
  return contentType;
};
